import React from 'react';
import ButtonNew from '../ui/ButtonNew';

const ComponentsShowcase = () => {
    // Sample icons for demonstration
    const PlusIcon = () => (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );

    const ArrowIcon = () => (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const Section = ({ title, children }) => (
        <div className="mb-12">
            <h2 className="text-2xl font-bold text-primaryText mb-6">{title}</h2>
            <div className="space-y-6">{children}</div>
        </div>
    );

    const ButtonGroup = ({ title, children }) => (
        <div className="mb-8">
            <h3 className="text-lg font-semibold text-primaryText mb-4">{title}</h3>
            <div className="flex flex-wrap gap-4 items-center">{children}</div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#f6f6f6] p-8">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-4xl font-bold text-primaryText mb-8">Button Component Showcase</h1>
                <p className="text-secondaryText mb-12">
                    All variations of the ButtonNew component based on the Figma Design System
                </p>

                {/* Color Variations */}
                <Section title="Color Variations">
                    {[
                        { key: 'blue', label: 'Blue (Primary)' },
                        { key: 'orange', label: 'Orange' },
                        { key: 'neutral', label: 'Neutral (White / Gray)' },
                        { key: 'ghostGray', label: 'Ghost Gray' },
                        { key: 'ghostBlue', label: 'White with Blue Text' },
                        { key: 'danger', label: 'Danger' },
                        { key: 'share', label: 'Share / Success' },
                    ].map(({ key, label }) => (
                        <ButtonGroup key={key} title={label}>
                            <ButtonNew variant={key}>Primary</ButtonNew>
                            <ButtonNew variant={key} disabled>
                                Disabled
                            </ButtonNew>
                        </ButtonGroup>
                    ))}
                </Section>

                {/* Size Variations */}
                <Section title="Size Variations">
                    <ButtonGroup title="Scale">
                        {[
                            { size: 'xs', label: 'XS' },
                            { size: 'sm', label: 'SM' },
                            { size: 'md', label: 'MD' },
                            { size: 'lg', label: 'LG' },
                        ].map(({ size, label }) => (
                            <ButtonNew key={size} variant="blue" size={size} leftIcon={<PlusIcon />}>
                                {label}
                            </ButtonNew>
                        ))}
                    </ButtonGroup>
                </Section>

                {/* Shape Variations */}
                <Section title="Shape Variations">
                    <ButtonGroup title="Rounded">
                        <ButtonNew variant="blue" shape="rounded">
                            Rounded
                        </ButtonNew>
                        <ButtonNew variant="orange" shape="rounded">
                            Rounded
                        </ButtonNew>
                        <ButtonNew variant="neutral" shape="rounded">
                            Rounded
                        </ButtonNew>
                    </ButtonGroup>
                    <ButtonGroup title="Oval">
                        <ButtonNew variant="blue" shape="oval">
                            Oval Blue
                        </ButtonNew>
                        <ButtonNew variant="orange" shape="oval">
                            Oval Orange
                        </ButtonNew>
                        <ButtonNew variant="share" shape="oval">
                            Oval Share
                        </ButtonNew>
                    </ButtonGroup>
                </Section>

                {/* With Icons */}
                <Section title="With Icons">
                    <ButtonGroup title="With Start Icon">
                        <ButtonNew variant="blue" leftIcon={<PlusIcon />}>
                            Add Item
                        </ButtonNew>
                        <ButtonNew variant="orange" leftIcon={<PlusIcon />}>
                            Add Item
                        </ButtonNew>
                        <ButtonNew variant="neutral" leftIcon={<PlusIcon />}>
                            Add Item
                        </ButtonNew>
                    </ButtonGroup>

                    <ButtonGroup title="With End Icon">
                        <ButtonNew variant="blue" rightIcon={<ArrowIcon />}>
                            Continue
                        </ButtonNew>
                        <ButtonNew variant="orange" rightIcon={<ArrowIcon />}>
                            Continue
                        </ButtonNew>
                        <ButtonNew variant="ghostBlue" rightIcon={<ArrowIcon />}>
                            Continue
                        </ButtonNew>
                    </ButtonGroup>

                    <ButtonGroup title="With Both Icons">
                        <ButtonNew variant="blue" leftIcon={<PlusIcon />} rightIcon={<ArrowIcon />}>
                            Primary
                        </ButtonNew>
                        <ButtonNew variant="orange" leftIcon={<PlusIcon />} rightIcon={<ArrowIcon />}>
                            Primary
                        </ButtonNew>
                        <ButtonNew variant="danger" leftIcon={<PlusIcon />} rightIcon={<ArrowIcon />}>
                            Primary
                        </ButtonNew>
                    </ButtonGroup>
                </Section>

                {/* State Variations */}
                <Section title="State Variations">
                    <ButtonGroup title="Interactive States (Hover/Active)">
                        <div className="space-y-4">
                            <div>
                                <p className="text-sm text-secondaryText mb-2">Default State</p>
                                <ButtonNew variant="blue">Hover me</ButtonNew>
                            </div>
                            <div>
                                <p className="text-sm text-secondaryText mb-2">Hover State (hover to see)</p>
                                <ButtonNew variant="orange">Hover me</ButtonNew>
                            </div>
                            <div>
                                <p className="text-sm text-secondaryText mb-2">Active State (click to see)</p>
                                <ButtonNew variant="danger">Click me</ButtonNew>
                            </div>
                            <div>
                                <p className="text-sm text-secondaryText mb-2">Disabled State</p>
                                <ButtonNew variant="ghostBlue" disabled>
                                    Disabled
                                </ButtonNew>
                            </div>
                        </div>
                    </ButtonGroup>
                </Section>

                {/* All Combinations */}
                <Section title="All Color & Size Combinations">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {['blue', 'orange', 'neutral', 'ghostGray', 'ghostBlue', 'danger', 'share'].map((variant) => (
                            <div key={variant} className="bg-grayBgNormal p-4 rounded-lg border border-primaryBorder">
                                <h4 className="font-semibold text-primaryText mb-3 capitalize">{variant}</h4>
                                <div className="space-y-3">
                                    {['xs', 'sm', 'md', 'lg'].map((size) => (
                                        <div key={size} className="flex items-center gap-3">
                                            <span className="text-xs text-secondaryText w-16 capitalize">{size}:</span>
                                            <ButtonNew variant={variant} size={size}>
                                                Button
                                            </ButtonNew>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </Section>
            </div>
        </div>
    );
};

export default ComponentsShowcase;


